import { Controller } from "@hotwired/stimulus"

// Animated demos on the public landing. One controller per demo, `kind`
// picks the script: `terminal` types a command and reveals the response
// lines; `kyc` types a cédula then runs the verify rows; `verify` runs the
// rows alone; `crm` fills fields one by one. Every demo loops, the replay
// button restarts it, and prefers-reduced-motion jumps to the final frame.
export default class extends Controller {
  static targets = ["cmd", "caret", "resp", "status", "pending", "verdict", "field", "query", "cell", "synced"]
  static values = { kind: String, command: String, query: String }

  connect() {
    this.timers = new Set()
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.reduced ? this.finish() : this.cycle()
  }

  disconnect() { this.clear() }

  replay() {
    this.clear()
    this.cycle()
  }

  // ── scripts ──
  cycle() {
    this.reset()
    switch (this.kindValue) {
      case "terminal": return this.terminal()
      case "kyc": return this.kyc()
      case "crm": return this.crm()
      default: return this.verify(500)
    }
  }

  terminal() {
    const cmd = this.commandValue
    let i = 0
    const type = () => {
      if (i <= cmd.length) {
        this.cmdTarget.textContent = cmd.slice(0, i)
        i += 2
        return this.after(34, type)
      }
      this.caretTarget.classList.add("hidden")
      this.respTargets.forEach((line, j) => this.after(420 + j * 110, () => line.classList.remove("hidden")))
      const done = 420 + this.respTargets.length * 110 + 200
      this.after(done, () => this.setStatus(true))
      this.after(done + 7400, () => this.cycle())
    }
    this.after(600, type)
  }

  kyc() {
    const q = this.queryValue
    let i = 0
    this.fieldTarget.classList.add("ring-2", "ring-panadata-primary/30", "border-panadata-primary")
    const type = () => {
      if (i <= q.length) {
        this.queryTarget.textContent = q.slice(0, i)
        this.queryTarget.classList.remove("font-sans", "text-gray-400")
        i += 1
        return this.after(70, type)
      }
      this.fieldTarget.classList.remove("ring-2", "ring-panadata-primary/30", "border-panadata-primary")
    }
    this.after(300, type)
    this.verify(500 + q.length * 70 + 300)
  }

  verify(startAt) {
    this.pendingTargets.forEach((row, i) => {
      const at = startAt + i * 1050
      this.after(at, () => row.classList.replace("hidden", "inline-flex"))
      this.after(at + 850, () => {
        row.classList.replace("inline-flex", "hidden")
        this.verdictTargets[i].classList.remove("hidden")
      })
    })
    this.after(startAt + this.pendingTargets.length * 1050 + 5600, () => this.cycle())
  }

  crm() {
    this.cellTargets.forEach((cell, i) => this.after(700 + i * 750, () => this.fill(cell)))
    const done = 700 + this.cellTargets.length * 750 + 250
    this.after(done, () => this.syncedTargets.forEach(el => el.classList.remove("hidden")))
    this.after(done + 6400, () => this.cycle())
  }

  // ── frames ──
  reset() {
    this.clear()
    if (this.hasCmdTarget) { this.cmdTarget.textContent = ""; this.caretTarget.classList.remove("hidden") }
    this.respTargets.forEach(line => line.classList.add("hidden"))
    if (this.hasStatusTarget) this.setStatus(false)
    this.pendingTargets.forEach(row => row.classList.replace("inline-flex", "hidden"))
    this.verdictTargets.forEach(v => v.classList.add("hidden"))
    if (this.hasQueryTarget) {
      this.queryTarget.textContent = "Cédula o RUC del cliente…"
      this.queryTarget.classList.add("font-sans", "text-gray-400")
    }
    this.cellTargets.forEach(cell => { cell.innerHTML = '<span class="text-gray-400">—</span>' })
    this.syncedTargets.forEach(el => el.classList.add("hidden"))
  }

  finish() {
    this.clear()
    if (this.hasCmdTarget) { this.cmdTarget.textContent = this.commandValue; this.caretTarget.classList.add("hidden") }
    this.respTargets.forEach(line => line.classList.remove("hidden"))
    if (this.hasStatusTarget) this.setStatus(true)
    this.pendingTargets.forEach(row => row.classList.replace("inline-flex", "hidden"))
    this.verdictTargets.forEach(v => v.classList.remove("hidden"))
    if (this.hasQueryTarget) {
      this.queryTarget.textContent = this.queryValue
      this.queryTarget.classList.remove("font-sans", "text-gray-400")
    }
    this.cellTargets.forEach(cell => this.fill(cell))
    this.syncedTargets.forEach(el => el.classList.remove("hidden"))
  }

  fill(cell) {
    const chip = cell.dataset.chipClass
    const value = cell.dataset.value
    if (chip) {
      const span = document.createElement("span")
      span.className = chip
      span.textContent = value
      cell.replaceChildren(span)
    } else {
      cell.textContent = value
    }
  }

  setStatus(ok) {
    const el = this.statusTarget
    el.className = ok ? el.dataset.okClass : el.dataset.idleClass
    el.textContent = ok ? "200 OK" : "· · ·"
  }

  // ── clock ──
  after(ms, fn) {
    const id = setTimeout(() => { this.timers.delete(id); fn() }, ms)
    this.timers.add(id)
  }

  clear() {
    this.timers.forEach(clearTimeout)
    this.timers.clear()
  }
}
